<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useDeviceStore } from '@/stores/device'
import { useUserStore } from '@/stores/user'
import { useFirmwareStore } from '@/stores/firmware'

const { t } = useI18n()
const router = useRouter()
const deviceStore = useDeviceStore()
const userStore = useUserStore()
const firmwareStore = useFirmwareStore()
const props = withDefaults(
  defineProps<{
    alwaysVisible?: boolean
  }>(),
  {
    alwaysVisible: false,
  }
)
userStore.loadFromStorage()
const showUserMenu = ref(false)
const emit = defineEmits<{
  (e: 'open-hotkey'): void
}>()

const batteryDisplay = computed(() => `${deviceStore.batteryLevel ?? '--'}%`)

const connectionTypeText = computed(() => {
  return deviceStore.connectionType || '2.4G'
})

const connectionIcon = computed(() => {
  return connectionTypeText.value === 'USB' ? '/img/header/link_USB.png' : '/img/header/link_2.4G.png'
})

const openHotkeyDialog = () => {
  emit('open-hotkey')
}

const isLoggedIn = computed(() => userStore.isLoggedIn)
const username = computed(() => userStore.username || 'User')
const avatarSrc = computed(() => {
  const avatar = userStore.avatar || '/img/userCenter/userAvatar.png'
  if (avatar.startsWith('data:') || avatar.startsWith('blob:')) return avatar
  const [url, hash = ''] = avatar.split('#', 2)
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}t=${Date.now()}${hash ? `#${hash}` : ''}`
})

const openUserMenu = () => {
  if (isLoggedIn.value) showUserMenu.value = true
}

const closeUserMenu = () => {
  showUserMenu.value = false
}

const goLogin = () => {
  router.push('/login')
}

const goUserCenter = () => {
  showUserMenu.value = false
  router.push('/user')
}

const goUpgrade = () => {
  showUserMenu.value = false
  router.push({ path: '/user', query: { tab: 'firmware' } })
}

const logout = () => {
  userStore.logout()
  showUserMenu.value = false
  router.push('/login')
}

const showAbout = () => {
  showUserMenu.value = false
}
</script>

<template>
  <header v-if="props.alwaysVisible || deviceStore.isConnected" class="app-header">
    <div class="header-left"></div>

    <div class="header-right">
      <div v-if="deviceStore.isConnected && deviceStore.isHeadPhone" class="header-item battery-status">
        <span v-if="deviceStore.isCharging" class="charging-icon">
          <img src="/img/header/battery_charging.png" alt="charging">
        </span>
        <div class="battery-display">
          <div class="battery-icon">
            <div
              class="battery-fill"
              :class="{ low: (deviceStore.batteryLevel || 0) <= 20, medium: (deviceStore.batteryLevel || 0) > 20 && (deviceStore.batteryLevel || 0) <= 30 }"
              :style="{ transform: `scaleX(${Math.max(0, Math.min(100, deviceStore.batteryLevel || 0)) / 100})` }"
            ></div>
          </div>
          <span class="text">{{ batteryDisplay }}</span>
        </div>
      </div>

      <div v-if="deviceStore.isConnected" class="header-item connection-type">
        <img :src="connectionIcon" :alt="connectionTypeText" class="icon">
        <span class="text">{{ connectionTypeText }}</span>
      </div>

      <div v-if="deviceStore.canUseHotkey" class="header-item hotkey" @click="openHotkeyDialog">
        <img src="/img/header/hotKey.png" alt="hotkey" class="icon">
        <span class="text">{{ t('header.hotkey', 'Hotkeys') }}</span>
      </div>

      <div
        class="user-box"
        :class="{ 'is-logged-in': isLoggedIn }"
        @mouseenter="openUserMenu"
        @mouseleave="closeUserMenu"
      >
        <button v-if="!isLoggedIn" type="button" class="login-register-btn" @click="goLogin">
          {{ t('auth.login') }}/{{ t('auth.register') }}
        </button>

        <div v-else class="user-trigger">
          <img class="user-avatar" :src="avatarSrc" alt="avatar">
          <span class="username">{{ username }}</span>
          <el-icon class="down" :class="{ open: showUserMenu }"><ArrowDown /></el-icon>

          <div v-if="showUserMenu" class="user-menu">
            <div class="item" @click.stop="goUserCenter">{{ t('user.profile') }}</div>
            <div class="item firmware-menu-item" @click.stop="goUpgrade">
              {{ t('header.firmwareUpgrade') }}
              <span v-if="firmwareStore.anyUpdate" class="firmware-menu-update-dot"></span>
            </div>
            <div class="item" @click.stop="showAbout">{{ t('header.aboutUs') }}</div>
            <div class="item" @click.stop="logout">{{ t('nav.logout') }}</div>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<style scoped lang="scss">
.app-header {
  position: fixed;
  top: 0;
  left: var(--sidebar-width);
  right: 0;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  z-index: 99;
}

.header-left,
.header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 10px;
  border-radius: 20px;
  transition: background 0.3s;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  .icon {
    width: 15px;
    height: 15px;
    object-fit: contain;
    opacity: 0.8;
  }

  .text {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.75);
  }
}

.battery-display {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.battery-icon {
  position: relative;
  width: 16px;
  height: 10px;
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 2px;
}

.battery-icon::after {
  content: '';
  position: absolute;
  right: -3px;
  top: 50%;
  transform: translateY(-50%);
  width: 2px;
  height: 2px;
  border-top: 1px solid rgba(255, 255, 255, 0.6);
  border-right: 1px solid rgba(255, 255, 255, 0.6);
  border-bottom: 1px solid rgba(255, 255, 255, 0.6);
}

.battery-fill {
  position: absolute;
  top: 1px;
  bottom: 1px;
  left: 1px;
  right: 1px;
  background: rgba(2, 237, 10, 1);
  border-radius: 1px;
  transform-origin: left;
}

.battery-fill.low {
  background: #ef4444;
}

.battery-fill.medium {
  background: #f59e0b;
}

.charging-icon img {
  width: 15px;
  height: 15px;
}

.user-box {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 10px;
  min-height: 48px;
  cursor: pointer;
}

.login-register-btn {
  min-width: 128px;
  height: 34px;
  padding: 0 24px;
  border: 0;
  border-radius: 999px;
  background: rgba(42, 42, 44, 1);
  color: rgba(255, 255, 255, 0.86);
  font-size: 14px;
  line-height: 34px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: rgba(56, 56, 60, 1);
    color: #fff;
  }
}

.user-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  padding: 0 0 0 2px;
}

.user-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: block;
  object-fit: cover;
}

.username {
  max-width: 138px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  line-height: 20px;
  color: rgba(255, 255, 255, 0.68);
}

.down {
  color: rgba(255, 255, 255, 0.45);
  font-size: 12px;
  transition: transform 0.18s;
}

.down.open {
  transform: rotate(180deg);
}

.user-menu {
  position: absolute;
  top: 48px;
  right: 0;
  width: 212px;
  min-height: 160px;
  padding: 8px 0;
  background: rgba(34, 34, 36, 1);
  border-radius: 7px;
  overflow: hidden;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.35);
  z-index: 1000;
}

.user-menu .item {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 28px;
  font-size: 14px;
  line-height: 36px;
  color: rgba(255, 255, 255, 0.62);
  text-align: center;
  white-space: nowrap;
  transition: background 0.2s, color 0.2s;
}

.user-menu .item:hover {
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.95);
}

.firmware-menu-update-dot {
  position: absolute;
  top: 15px;
  right: 42px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #ff4d4f;
}

@media (max-width: 900px) {
  .header-right {
    gap: 4px;
  }
}
</style>
